// --- React
import React from 'react';
import {useSelector} from 'react-redux';
// --- UI Components
import Button from '@dtci/spellbook/Button';
import {useAgGridApi} from '../../../../components/AGGrid/hooks';
import {ProductDetailsDrawer} from '../details-drawer';
// --- Dialogs & Modals
import {DetailsModal} from '../../../../components/DetailsModal';
import {ViewCommentsModal} from '../../../../components/CommentModals/ViewCommentsModal';
import {EditCommentModal} from '../../../../components/CommentModals/EditCommentModal';
import {CreateCommentModal} from '../../../../components/CommentModals/CreateCommentModal';
import {AttachmentsModal} from '../../../../components/CommentModals/AttachmentsModal';
import {useLegalStatusDialog} from '../../../../components/dialogs/dialogs/legalStatus';
import {useContractualPartiesDialog} from '../../../../components/dialogs/dialogs/contractualParties';
import {useCopyToDialog} from '../../../../components/dialogs/dialogs/copy';
import {useVersionDialog} from '../../../../components/dialogs/dialogs/version';
import {useDeleteDialog} from '../../../../components/dialogs/dialogs/delete';
// --- API & Utilities
import {datastageApi} from '../../../../api';
import {filterCriteria} from '../../../../utils/filterCriteria';
import {useLoading} from '../../../../utils/hooks/useLoading';
// --- Redux
import {useProductsActions} from '../../../../store/products/useProducts';
import {useCommentsActions} from '../../../../store/comments/useComments';
import {useContactsActions} from '../../../../store/contacts/useContacts';
import {useToastNotification} from '../../../../components/ToastNotifications';
import {startJobStatusTracker} from '../../../../utils/jobStatusTracker';
import {useClearanceMemoTitlesActions} from '../../../../store/clearanceMemoTitles/useClearanceMemoTitles';
import {MemoForm} from "../../../../components/ClearanceMemo/memo-form";
import {EditSectionModal} from "../../../../components/ClearanceMemo/memo-form/edit-modal";
import DataGrid from "@dtci/spellbook/DataGrid";
import styles from "../../../../components/AGGrid/details-table.module.scss";
import {usePermissions} from "../../../../utils/permissions";
import { useProductsGridUtils } from './utils/gridUtils';

/**
 * ProductsGrid Component
 *
 * This component is responsible for displaying a grid of products.
 * It also includes a drawer for viewing product details
 * and modals for viewing/editing comments and attachments.
 *
 * @component
 * @returns {JSX.Element} Products grid.
 */
export const ProductsGrid = () => {
    const agGridApi = useAgGridApi();
    const {isLegalUser, isLegalAdmin} = usePermissions();
    const {updateProducts} = useProductsActions();
    const {updateComments, updateCommentsFilterForm} = useCommentsActions();
    const {updateClearanceMemoTitles} = useClearanceMemoTitlesActions();
    const {updateContacts} = useContactsActions();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {products, defaultFilterRange, productRange, productSort, filterForm} = useSelector(
        (s) => s.products,
    );
    const {isMemoModalOpen} = useSelector(
        (s) => s.titles,
    );
    const {filterForm: commentFilterForm} = useSelector(
        (s) => s.comments,
    );

    const {openLegalStatusDialog} = useLegalStatusDialog();
    const {openContractualPartiesDialog} = useContractualPartiesDialog();
    const {openCopyToDialog} = useCopyToDialog();
    const {openVersionDialog} = useVersionDialog();
    const {openDeleteDialog} = useDeleteDialog();
    const {openToast} = useToastNotification();

    const {
        setSort,
        columns
    } = useProductsGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateProducts,
        isLegalUser,
        isLegalAdmin,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: [
                (data) => () => onOpenCommentsModal(data),
                (data) => () => onOpenDetailsDrawer(data),
                (data) => () => onOpenLegalStatusModal(data),
                (data) => () => onOpenContractualPartiesModal(data),
                (data) => () => onOpenCopyToModal(data),
                (data) => () => onOpenVersionModal(data),
                (data) => () => onOpenDeleteModal(data),
                (data) => () => onOpenCreateModal(data),
                (data) => () => openEditModal(data),
                (data) => () => openViewModal(data)
            ]
        }
    });

    // Function to refresh products
    const refreshProducts = async () => {
        try {
            // Fetch products from the API
            const data = await datastageApi.products(
                filterCriteria(filterForm, '', 'products'),
                productRange,
                productSort,
            );

            // Update products in the store with the fetched data
            updateProducts({
                products: data.content,
                totalPages: data.totalPages,
                totalItems: data.totalElements,
                currentPage: data.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error refreshing products:', error);
        }
    };

    const onOpenDetailsDrawer = (data) => {
        // Open the details drawer for a specific product
        updateProducts({
            isDetailsOpen: true,
            details: data,
        });
    };

    // Open the legal status modal for a specific product
    const onOpenLegalStatusModal = (data) => {
        openLegalStatusDialog(data, async ({legalStatus}) => {
            setLoadingTrue();
            try {
                const res = await datastageApi.updateLegalStatus(data.id, legalStatus);
                if (res?.status === 200) {
                    await refreshProducts();
                    openToast('Saved Legal Status', 'success');
                } else {
                    openToast(
                        `${(res?.statusText && res?.statusText) || 'Error saving Legal Status'}`,
                        'error',
                    );
                }
            } catch (error) {
                console.error('Error saving Legal Status:', error);
                openToast(`Error saving Legal Status`, 'error');
            } finally {
                setLoadingFalse();
            }
        });
    };

    // Open the contractual parties modal for a specific product
    /**
     * Opens the modal for managing contractual parties for a product.
     * - Fetches contacts and contractual party types from the API.
     * - Updates the store with fetched data.
     * - Opens the contractual parties dialog for selection and updates the product data.
     * - Implements error handling to prevent unexpected failures.
     *
     * @param {Object} data - The product data.
     */
    const onOpenContractualPartiesModal = async (data) => {
        try {
            setLoadingTrue();

            // Fetch the list of contacts with Rightsline IDs
            const contactsResponse = await datastageApi.contacts(
                {showOnlyWithRightslineIds: true},
                [0,19],
                ['id', 'DESC'],
            );

            // Fetch the list of contractual party types
            const contractualPartyTypes = await datastageApi.contractualPartyTypes();

            // Map the contacts to the required format
            const contacts = contactsResponse.content.map((x) => ({
                label: x.name,
                value: x.id,
            }));

            // Update store with fetched contacts and contractual party types
            updateContacts({contractualParties: contacts, contractualPartyTypes});
            updateProducts({cmContractualParties: data.clearanceMemoContractualParties});
        } catch (error) {
            console.error('Error loading contractual parties data:', error);
        } finally {
            setLoadingFalse();
        }

        // Open the contractual parties dialog
        openContractualPartiesDialog(data, async (clearanceMemoContractualParties) => {
            try {
                setLoadingTrue();

                // Update the contractual parties for the product
                const res = await datastageApi.updateContractualParties(
                    data.productId,
                    clearanceMemoContractualParties,
                );

                if (res?.status === 200) {
                    // Refresh the product list
                    await refreshProducts();
                    openToast('Saved Contractual Parties', 'success');
                } else {
                    openToast(
                        `${
                            (res?.statusText && res?.statusText) ||
                            'Error saving Contractual Parties'
                        }`,
                        'error',
                    );
                }
            } catch (error) {
                console.error('Error saving contractual parties:', error);
                openToast('Error saving Contractual Parties', 'error');
            } finally {
                setLoadingFalse();
            }
        });
    };

    // Open the copy to modal for a specific product
    const onOpenCopyToModal = async (data) => {
        setLoadingTrue();
        try {
            // Fetch products without a clearance memo
            const res = await datastageApi.productsToCopyTo(
                {hasClearanceMemo: false},
                [0, 9],
                ['productTitle', 'ASC'],
            );
            const options = res.content.map((x) => ({
                label: x.groupTitle || 'Unnamed Product',
                value: x.id,
            }));
            updateProducts({productOptions: options});
        } catch (error) {
            console.error('Error fetching product options:', error);
            setLoadingFalse();
            return;
        }
        setLoadingFalse();

        openCopyToDialog(data, async ({copyTo}) => {
            setLoadingTrue();
            try {
                const res = await datastageApi.copyClearanceMemo(data.id, copyTo);

                startJobStatusTracker(res?.data?.id, async (message, status) => {
                    setLoadingTrue();
                    openToast(message, status);

                    if (status === 'success') {
                        await refreshProducts();
                    }

                    setLoadingFalse();
                });

                setLoadingFalse();
            } catch (error) {
                console.error('Error copying clearance memo:', error);
                openToast('Failed to copy clearance memo.', 'error');
                setLoadingFalse();
            }
        });
    };

    // Open the version modal for a specific product
    const onOpenVersionModal = async (data) => {
        try {
            setLoadingTrue();

            // Fetch the list of versions for the product
            const ver = await datastageApi.versions(data.productId);

            // Update the store with the fetched versions
            updateProducts({versions: ver});
        } catch (error) {
            console.error('Error fetching product versions:', error);
        } finally {
            setLoadingFalse();
        }

        // Open the version dialog
        openVersionDialog(data, async () => {
        });
    };

    // Open the delete modal for a specific product
    const onOpenDeleteModal = (data) => {
        try {
            openDeleteDialog(
                'Yes, Delete Clearance Memo',
                [
                    `Are you sure you want to permanently delete the Clearance Memo and Clearance Report for ${data.title} (${data.productId})?`,
                    'Please note: This will also remove all product comments and attachments on this title. This action cannot be reversed.',
                ],
                async () => {
                    try {
                        setLoadingTrue();

                        // Delete the clearance memo for the product
                        const res = await datastageApi.deleteClearanceMemo(data.productId);

                        console.log(res)
                        if (res?.status === 200) {
                            // Refresh the product list
                            await refreshProducts();

                            openToast('Clearance Memo deleted successfully', 'success');
                        } else {
                            openToast(
                                `${
                                    (res?.statusText && res?.statusText) ||
                                    'Failed to delete clearance memo'
                                }`,
                                'error',
                            );
                        }
                    } catch (error) {
                        console.error('Error deleting clearance memo:', error);
                        openToast('Failed to delete clearance memo', 'error');
                    } finally {
                        setLoadingFalse();
                    }
                },
            );
        } catch (error) {
            console.error('Error in onOpenDeleteModal:', error);
        }
    };

    // Open the create modal for a specific product
    const onOpenCreateModal = async (data) => {
        try {
            setLoadingTrue();

            // Create a new clearance memo for the product
            await datastageApi.createClearanceMemo(data.productId);

            // Fetch title by product ID
            const title = await datastageApi.titleByMappedProductId(data.productId);

            // Fetch the clearance memo TOC for the product
            const toc = await datastageApi.clearanceMemoTOC(title.id);

            updateClearanceMemoTitles({
                isMemoModalOpen: true,
                title: title,
                isViewOnly: false,
                clearanceMemoTOC: toc,
                product: data,
            });
        } catch (error) {
            console.error('Error creating clearance memo:', error);
        } finally {
            await refreshProducts();
            setLoadingFalse();
        }
    };

    // Open the Edit Clearance Memo modal for a specific product
    const openEditModal = async (data) => {
        try {
            setLoadingTrue();

            // Fetch the title by product ID
            const title = await datastageApi.titleByMappedProductId(data.productId);

            // Fetch the clearance memo TOC for the product
            const toc = await datastageApi.clearanceMemoTOC(title.id);

            updateClearanceMemoTitles({
                isMemoModalOpen: true,
                isViewOnly: false,
                title,
                clearanceMemoTOC: toc,
                product: data,
            });
        } catch (error) {
            console.error('Error opening memo modal:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const openViewModal = async (data) => {
        try {
            setLoadingTrue();

            // Fetch the title by product ID
            const title = await datastageApi.titleByMappedProductId(data.productId);

            // Fetch the clearance memo TOC for the product
            const toc = await datastageApi.clearanceMemoTOC(title.id);

            //navigate('/clearanceMemoTitles');
            updateClearanceMemoTitles({
                isMemoModalOpen: true,
                isViewOnly: true,
                title,
                clearanceMemoTOC: toc,
                product: data,
            });
        } catch (error) {
            console.error('Error opening memo modal:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleCloseMemoModal = () => {
        // Close the Clearance Memo modal
        updateClearanceMemoTitles({
            isMemoModalOpen: false,
        });
    };

    // Open the comments modal for a specific product
    const onOpenCommentsModal = async (data) => {

        setLoadingTrue();

        const commentProduct = {
            productId: data?.productId,
            foxFinancialTitleId: data?.foxFinancialTitleId,
            clearanceMemoTitleId: data?.clearanceMemoTitleId,
        }

        // Fetch comments from API
        const commentList = await datastageApi.comments(
            filterCriteria(commentFilterForm, commentProduct, 'comments'),
            [0,19],
            ["commentType", "DESC"],
        );

        // Update comment filter form
        updateCommentsFilterForm({
            key: 'commentProduct',
            data: commentProduct,
        });

        // Update comment list and open comment modal with fetched comments
        updateComments({
            isCommentModalOpen: true,
            comments: commentList?.content || [],
            totalPages: commentList?.totalPages || 1,
            totalItems: commentList?.totalElements || 0,
            currentPage: commentList?.pageable?.pageNumber,
        });

        setLoadingFalse();
    };

    return (
        <>
            {/* AG Grid Table for displaying products */}
            <DataGrid
                className={styles.dataGrid}
                autoHeight
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: products,
                    headerHeight: 45,
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={'No Products'}
            />

            {/* Product Details Drawer for displaying product details */}
            <ProductDetailsDrawer/>
            <ViewCommentsModal/>
            <CreateCommentModal/>
            <EditCommentModal/>
            <AttachmentsModal/>
            <DetailsModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                modalHeader="Clearance Memo"
                footer={
                    <Button
                        iconType="fa-regular fa-xmark"
                        sizeType="small"
                        styleType="link"
                        onClick={handleCloseMemoModal}
                    >
                        Close
                    </Button>
                }
            >
                <MemoForm/>
            </DetailsModal>
            <EditSectionModal/>
        </>
    );
};
